/**
 * planTypes.service.ts — Admin > Plan Types: the account's plan types,
 * edited through /v5/plan-types as the corporate dashboard does. A delete sets
 * the type inactive; restore is an update back to active (there is no restore
 * route). Writes need Admin Access. (Pickers keep using admin.service's
 * listPlanTypes and productTypes.service's listPlanTypeOptions.)
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

export interface AdminPlanType {
  planTypeID: number;
  name: string;
  /** 0 = the default pipeline. */
  pipelineTypeID: number;
  pipelineTitle: string;
  restrictActiveQuote: boolean;
  active: boolean;
}

export interface PipelineTypeOption {
  pipelineTypeID: number;
  pipelineTitle: string;
}

export interface PlanTypePayload {
  name: string;
  pipelineTypeID: number;
  restrictActiveQuote: boolean;
}

function flag(v: unknown): boolean {
  return v === true || v === 1 || v === '1';
}

/** Every plan type, deleted ones too, in the account's order. */
export function listAdminPlanTypes(): Promise<AdminPlanType[]> {
  return apiClient.get<ApiResponse<Record<string, unknown>[]>>('/plan-types').then((r) =>
    (Array.isArray(r.data?.data) ? r.data.data : []).map((row) => {
      const pipeline = (row.pipelineType ?? null) as Record<string, unknown> | null;
      return {
        planTypeID: Number(row.planTypeID ?? 0),
        name: String(row.name ?? ''),
        pipelineTypeID: Number(row.pipelineTypeID ?? 0) || 0,
        pipelineTitle: pipeline ? String(pipeline.pipelineTitle ?? '') : '',
        restrictActiveQuote: flag(row.restrictActiveQuote),
        active: flag(row.active),
      };
    }),
  );
}

/** The account's live pipelines, for the Pipeline Type select. */
export function listPipelineTypeOptions(): Promise<PipelineTypeOption[]> {
  return apiClient.get<ApiResponse<Record<string, unknown>[]>>('/account-pipeline-types').then((r) =>
    (Array.isArray(r.data?.data) ? r.data.data : [])
      .filter((p) => String(p.status ?? '1') !== '0')
      .map((p) => ({ pipelineTypeID: Number(p.pipelineTypeID ?? 0), pipelineTitle: String(p.pipelineTitle ?? '') }))
      .filter((p) => p.pipelineTypeID > 0),
  );
}

/** A new type, created as corp does: not the default type, no credit. */
export function createPlanType(data: PlanTypePayload): Promise<void> {
  return apiClient.post('/plan-types', { ...data, defaultType: false, credit: 0 }).then(() => undefined);
}

export function updatePlanType(id: number, data: Partial<PlanTypePayload> & { active?: boolean }): Promise<void> {
  return apiClient.put(`/plan-types/${id}`, data).then(() => undefined);
}

export function deletePlanType(id: number): Promise<void> {
  return apiClient.delete(`/plan-types/${id}`).then(() => undefined);
}

export function restorePlanType(id: number): Promise<void> {
  return updatePlanType(id, { active: true });
}
